import { Suspense, type ReactNode } from 'react';
import { lazyPage } from './lazy-page';
import { Route, Routes } from 'react-router';
import { FullPageSpinner, RequireAccess } from '../app/guards';
import { AppLayout } from '../app/layouts/AppLayout';
import { PrintLayout } from '../app/layouts/PrintLayout';
import { PublicLayout } from '../app/layouts/PublicLayout';
import { StatusLayout } from '../app/layouts/StatusLayout';
import { Spinner } from '../components';
import NotFoundPage from '../pages/status/NotFoundPage';
import { ROUTES, type LayoutId, type RouteDef, type RouteId } from './manifest';
import { PAGES } from './pages';

const inPage = <Spinner label="Loading page…" />;
// the style guide is a developer page: never part of the initial bundle (it pulls in demo forms)
const DesignSystemPage = lazyPage(() => import('../design-system/DesignSystemPage'));

function page(r: RouteDef): ReactNode {
  if (r.id === 'design-system')
    return (
      <Suspense fallback={inPage}>
        <DesignSystemPage />
      </Suspense>
    );
  const Page = PAGES[r.id as Exclude<RouteId, 'design-system'>];
  // public pages suspend to the layout's boundary (no spinner inside the panel, see PublicLayout)
  if (r.layout === 'public')
    return (
      <RequireAccess access={r.access}>
        <Page />
      </RequireAccess>
    );
  return (
    <RequireAccess access={r.access}>
      <Suspense fallback={inPage}>
        <Page />
      </Suspense>
    </RequireAccess>
  );
}

const byLayout = (layout: LayoutId) =>
  (ROUTES as readonly RouteDef[]).filter((r) => r.layout === layout);
const routes = (layout: LayoutId) =>
  byLayout(layout).map((r) => <Route key={r.id} path={r.path} element={page(r)} />);

/**
 * The route tree, derived from manifest.ts: one layout route per layout with its pages nested.
 * Unknown paths → 404 page (nginx sends the 404 status for them before the SPA loads).
 */
export function AppRoutes() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route element={<AppLayout />}>{routes('app')}</Route>
        <Route element={<PublicLayout />}>{routes('public')}</Route>
        <Route element={<PrintLayout />}>{routes('print')}</Route>
        {byLayout('status').map((r) => (
          <Route
            key={r.id}
            path={r.path}
            element={
              <StatusLayout title={r.title}>
                <Suspense fallback={inPage}>{page(r)}</Suspense>
              </StatusLayout>
            }
          />
        ))}
        {routes('none')}
        <Route
          path="*"
          element={
            <StatusLayout title="Page not found">
              <NotFoundPage />
            </StatusLayout>
          }
        />
      </Routes>
    </Suspense>
  );
}
